import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import { LibraryIcon } from '@components/Icons/LibraryIcon';
import { USBIcon } from '@components/Icons/USBIcon';
import { VideoSelectedIcon } from '@components/Icons/VideoSelectedIcon';
import { HIGH_USAGE_WARNING_THRESHOLD, ImpactPopup } from '@components/MediaUploadBox/ImpactPopup';
import { OwnerAffordability, ownerShortfallOf } from '@lib/utils/ownerAffordability';
import { formatCredits, formatDurationToString } from '@lib/utils/units';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { BilledToOwnerNotice } from './BilledToOwnerNotice';
import css from './ChooseMediaModalFooter.module.scss';

type ChooseMediaModalFooterProps = {
  nSelected: {
    total: number; // Total number of items selected
    filteredOut: number; // Number of selected items that are filtered out by DataTable filters
    alreadyInLibrary: number; // Number of selected items that are already in the library
  };
  creditStats: {
    current: number; // Current credits available (in API credits, will be formatted for display)
    cost: number; // Cost in credits for the selected items (in API credits, will be formatted for display)
    left: number; // Credits remaining after processing (in API credits, will be formatted for display)
  };
  storageStats: {
    current: number; // Current storage available in seconds
    cost: number; // Storage cost in seconds (`IngestEstimate.durationSeconds`)
    left: number; // Storage remaining after processing in seconds
  };
  hasUnavailableInfo?: boolean; // Whether some file info is unavailable (e.g., missing duration)
  mobile?: boolean; // Compact mode: only credit + storage badges, no media count or hover popups
  /**
   * True when the library's owner is billed instead of the current user. The cost badges stay —
   * the estimate is a property of the media — but everything measured against the uploader's own
   * balance drops out: the "/ available" suffix on mobile, the insufficient/high-usage colouring,
   * and the balance rows inside the hover popups. The owner notice is added alongside.
   */
  billedToAnotherAccount?: boolean;
  /** The owner's name, when known, so the badge and the cost popups can name them. */
  ownerName?: string;
  /**
   * A shortfall escalates the badge in place. That badge is the only explanation a touch device
   * gets: the Add button's reason is tooltip-only (`.claude/rules/mobile-breakpoints.md`).
   */
  ownerAffordability?: OwnerAffordability;
};

export const ChooseMediaModalFooter = ({
  nSelected,
  creditStats,
  storageStats,
  hasUnavailableInfo = false,
  mobile = false,
  billedToAnotherAccount = false,
  ownerName,
  ownerAffordability,
}: ChooseMediaModalFooterProps) => {
  const [showCreditsPopup, setShowCreditsPopup] = useState(false);
  const [showStoragePopup, setShowStoragePopup] = useState(false);
  const [isCreditsExiting, setIsCreditsExiting] = useState(false);
  const [isStorageExiting, setIsStorageExiting] = useState(false);
  const creditsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const storageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle credits popup exit animation
  useEffect(() => {
    if (!showCreditsPopup && isCreditsExiting) {
      creditsTimeoutRef.current = setTimeout(() => {
        setIsCreditsExiting(false);
      }, 200); // Match animation duration
    } else if (showCreditsPopup) {
      setIsCreditsExiting(false);
      if (creditsTimeoutRef.current) {
        clearTimeout(creditsTimeoutRef.current);
        creditsTimeoutRef.current = null;
      }
    }
    return () => {
      if (creditsTimeoutRef.current) {
        clearTimeout(creditsTimeoutRef.current);
      }
    };
  }, [showCreditsPopup, isCreditsExiting]);

  // Handle storage popup exit animation
  useEffect(() => {
    if (!showStoragePopup && isStorageExiting) {
      storageTimeoutRef.current = setTimeout(() => {
        setIsStorageExiting(false);
      }, 200); // Match animation duration
    } else if (showStoragePopup) {
      setIsStorageExiting(false);
      if (storageTimeoutRef.current) {
        clearTimeout(storageTimeoutRef.current);
        storageTimeoutRef.current = null;
      }
    }
    return () => {
      if (storageTimeoutRef.current) {
        clearTimeout(storageTimeoutRef.current);
      }
    };
  }, [showStoragePopup, isStorageExiting]);

  const handleCreditsMouseLeave = () => {
    if (showCreditsPopup) {
      setIsCreditsExiting(true);
      setShowCreditsPopup(false);
    }
  };

  const handleStorageMouseLeave = () => {
    if (showStoragePopup) {
      setIsStorageExiting(true);
      setShowStoragePopup(false);
    }
  };

  const creditCostFormatted = formatCredits(creditStats.cost, 'up');
  const totalDurationFormatted = formatDurationToString(storageStats.cost, {
    precision: mobile ? 1 : 0,
    detail: mobile ? 1 : 2,
  });

  // Add "≥ " prefix if some info is unavailable
  const creditDisplay = hasUnavailableInfo ? `≥ ${creditCostFormatted}` : creditCostFormatted;
  const storageDisplay = hasUnavailableInfo
    ? `≥ ${totalDurationFormatted}`
    : totalDurationFormatted;

  // Mobile: show "cost / available credit(s)" format. The "/ available" half is the uploader's
  // own balance, so it's dropped when the owner is the one being charged — leaving the cost.
  const currentCreditsFormatted = formatCredits(creditStats.current, 'down');
  const creditUnit = currentCreditsFormatted === '1' ? 'credit' : 'credits';
  const mobileCreditDisplay = billedToAnotherAccount
    ? creditDisplay
    : `${creditDisplay} / ${currentCreditsFormatted} ${creditUnit}`;
  const mobileStorageDisplay = billedToAnotherAccount
    ? storageDisplay
    : `${storageDisplay} / ${formatDurationToString(storageStats.current, { precision: 1, detail: 1 })}`;

  // Every flag below compares the cost against *this* user's balance, so none of them mean
  // anything for an owner-billed upload — a cost above our own balance is not a shortfall.
  // Keeping them false is what stops the badges going red over an upload we aren't paying for.
  //
  // Check if credits are insufficient (cost > current)
  const creditsInsufficient = !billedToAnotherAccount && creditStats.cost > creditStats.current;
  // Check if credits would consume more than threshold of available
  const creditsHighUsage =
    !billedToAnotherAccount &&
    creditStats.current > 0 &&
    creditStats.cost / creditStats.current > HIGH_USAGE_WARNING_THRESHOLD;

  // Check if storage is insufficient (cost > current)
  const storageInsufficient = !billedToAnotherAccount && storageStats.cost > storageStats.current;
  // Check if storage would consume more than threshold of available
  const storageHighUsage =
    !billedToAnotherAccount &&
    storageStats.current > 0 &&
    storageStats.cost / storageStats.current > HIGH_USAGE_WARNING_THRESHOLD;

  const nVisible = nSelected.total - nSelected.filteredOut;
  const showFilteredSeparator = nSelected.filteredOut > 0;
  const showLibrarySeparator = nSelected.alreadyInLibrary > 0;

  const filteredTooltipText =
    nVisible === 1
      ? '1 media to be added satisfies current filters'
      : `${nVisible} media to be added satisfy current filters`;

  const notVisibleTooltipContent = `${nSelected.filteredOut} media to be added not visible because of active filters.`;

  return (
    <span className={css.footerLeft}>
      <div className={classNames(css.bulletSeparated, { [css.centered]: mobile })}>
        {!mobile && (
          <div className={css.footerItem}>
            <span className={css.mediaCountBadge}>
              <Tooltip title={`${nSelected.total} new media to be added`} placement="top" arrow>
                <span className={css.tooltipWrapper}>
                  <VideoSelectedIcon />
                  <span className={css.mediaCountNumber}>{nSelected.total}</span>
                </span>
              </Tooltip>
              {showFilteredSeparator && (
                <>
                  <span className={css.separator}>|</span>
                  <span className={css.filteredInfo}>
                    <Tooltip title={filteredTooltipText} placement="top" arrow>
                      <span className={css.tooltipWrapper}>
                        <FilterAltIcon className={css.filterIcon} />
                        <span className={css.filteredCount}>{nVisible}</span>
                      </span>
                    </Tooltip>
                    <span className={css.iconSpacer} />
                    <Tooltip title={notVisibleTooltipContent} placement="top" arrow>
                      <span className={css.tooltipWrapper}>
                        <VisibilityOffIcon className={css.eyeIcon} />
                        <span className={css.filteredCount}>{nSelected.filteredOut}</span>
                      </span>
                    </Tooltip>
                  </span>
                </>
              )}
              {showLibrarySeparator && (
                <>
                  <span className={css.separator}>|</span>
                  <Tooltip
                    title={`${nSelected.alreadyInLibrary} media found in these URLs are already in library`}
                    placement="top"
                    arrow
                  >
                    <span className={css.tooltipWrapper}>
                      <span className={css.libraryIcon}>
                        <LibraryIcon size={14} />
                      </span>
                      <span className={css.filteredCount}>{nSelected.alreadyInLibrary}</span>
                    </span>
                  </Tooltip>
                </>
              )}
            </span>
          </div>
        )}
        <div
          className={classNames(css.footerItem, {
            [css.footerItemError]: creditsInsufficient,
            [css.footerItemWarning]: !creditsInsufficient && creditsHighUsage,
          })}
        >
          {mobile ? (
            <span className={css.creditBadge}>
              <CreditsPlusIcon />
              <span className={css.creditNumber}>{mobileCreditDisplay}</span>
            </span>
          ) : (
            <div
              className={css.hoverableBadge}
              onMouseEnter={() => setShowCreditsPopup(true)}
              onMouseLeave={handleCreditsMouseLeave}
            >
              <span className={css.creditBadge}>
                <CreditsPlusIcon />
                <span className={css.creditNumber}>{creditDisplay}</span>
              </span>
              {(showCreditsPopup || isCreditsExiting) && (
                <div
                  className={classNames(css.popupWrapper, {
                    [css.popupExiting]: isCreditsExiting,
                  })}
                  onMouseEnter={() => setShowCreditsPopup(true)}
                  onMouseLeave={handleCreditsMouseLeave}
                >
                  <ImpactPopup
                    data={{
                      credits: {
                        cost: creditStats.cost,
                        current: creditStats.current,
                        left: creditStats.left,
                      },
                    }}
                    className={css.costPopup}
                    hasUnavailableInfo={hasUnavailableInfo}
                    billedToAnotherAccount={billedToAnotherAccount}
                    ownerName={ownerName}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div
          className={classNames(css.footerItem, {
            [css.footerItemError]: storageInsufficient,
            [css.footerItemWarning]: !storageInsufficient && storageHighUsage,
          })}
        >
          {mobile ? (
            <span className={css.durationBadge}>
              <USBIcon />
              <span className={css.durationNumber}>{mobileStorageDisplay}</span>
            </span>
          ) : (
            <div
              className={css.hoverableBadge}
              onMouseEnter={() => setShowStoragePopup(true)}
              onMouseLeave={handleStorageMouseLeave}
            >
              <span className={css.durationBadge}>
                <USBIcon />
                <span className={css.durationNumber}>{storageDisplay}</span>
              </span>
              {(showStoragePopup || isStorageExiting) && (
                <div
                  className={classNames(css.popupWrapper, {
                    [css.popupExiting]: isStorageExiting,
                  })}
                  onMouseEnter={() => setShowStoragePopup(true)}
                  onMouseLeave={handleStorageMouseLeave}
                >
                  <ImpactPopup
                    data={{
                      storage: {
                        cost: storageStats.cost,
                        current: storageStats.current,
                        left: storageStats.left,
                      },
                    }}
                    className={css.costPopup}
                    hasUnavailableInfo={hasUnavailableInfo}
                    billedToAnotherAccount={billedToAnotherAccount}
                    ownerName={ownerName}
                  />
                </div>
              )}
            </div>
          )}
        </div>
        {billedToAnotherAccount && (
          <BilledToOwnerNotice
            variant="badge"
            ownerName={ownerName}
            shortfall={ownerShortfallOf(ownerAffordability)}
          />
        )}
      </div>
    </span>
  );
};
